import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import Navbar from '../../components/Navbar';
import { ChevronLeft, Shield, Plus, CheckCircle, Clock, AlertTriangle, XCircle, User, Search, Filter, Loader2, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const CLEARANCE_LEVELS = [
  { value: 'level_1', label: 'Level 1 - Community Volunteer' },
  { value: 'level_2', label: 'Level 2 - Youth Programs' },
  { value: 'level_3', label: 'Level 3 - Vulnerable Populations' },
  { value: 'level_4', label: 'Level 4 - Driver Clearance' },
  { value: 'level_5', label: 'Level 5 - Leadership / High Trust' }
];

const STATUS_STYLES = {
  verified: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
  under_review: { bg: 'bg-orange-100', text: 'text-orange-800', icon: AlertTriangle },
  revoked: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
  expired: { bg: 'bg-gray-100', text: 'text-gray-800', icon: Clock }
};

const BackgroundCheckManagement = () => {
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showInitiateModal, setShowInitiateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  
  // Form data
  const [initiateData, setInitiateData] = useState({
    volunteer_id: '',
    clearance_level: 'level_1',
    background_check_provider: ''
  });
  
  const [editData, setEditData] = useState({
    status: '',
    clearance_level: '',
    completion_date: '',
    expiration_date: '',
    nh_criminal_history_check: '',
    national_criminal_check: '',
    sex_offender_registry_check: '',
    child_abuse_registry_check: '',
    vulnerable_adult_registry_check: '',
    motor_vehicle_check: ''
  });

  const fetchChecks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/background-checks?${params.toString()}`);
      setChecks(response.data);
    } catch (error) {
      toast.error('Failed to load background checks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchChecks();
  }, [fetchChecks]);

  const handleInitiateCheck = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/background-checks`, initiateData);
      toast.success('Background check initiated');
      setShowInitiateModal(false);
      setInitiateData({ volunteer_id: '', clearance_level: 'level_1', background_check_provider: '' });
      fetchChecks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate background check');
    }
  };

  const handleUpdateCheck = async (e) => {
    e.preventDefault();
    try {
      const updatePayload = {};
      Object.keys(editData).forEach(key => {
        if (editData[key]) updatePayload[key] = editData[key];
      });
      
      await axios.put(`${API}/background-checks/${selectedCheck.id}`, updatePayload);
      toast.success('Background check updated');
      setShowEditModal(false);
      fetchChecks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update background check');
    }
  };

  const openEditModal = (check) => {
    setSelectedCheck(check);
    setEditData({
      status: check.status,
      clearance_level: check.clearance_level,
      completion_date: check.completion_date || '',
      expiration_date: check.expiration_date || '',
      nh_criminal_history_check: check.nh_criminal_history_check || '',
      national_criminal_check: check.national_criminal_check || '',
      sex_offender_registry_check: check.sex_offender_registry_check || '',
      child_abuse_registry_check: check.child_abuse_registry_check || '',
      vulnerable_adult_registry_check: check.vulnerable_adult_registry_check || '',
      motor_vehicle_check: check.motor_vehicle_check || ''
    });
    setShowEditModal(true);
  };

  const filteredChecks = checks.filter(check => 
    searchQuery === '' || 
    check.volunteer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    check.volunteer_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.pending;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  return (
    <div className="min-h-screen" data-testid="background-check-management">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/partner/dashboard" className="text-[#8B2E3C] hover:underline flex items-center gap-1 mb-4">
            <ChevronLeft className="w-4 h-4" />
            Back to Partner Dashboard
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-2">Background Checks</h1>
              <p className="text-base text-[#666666]">Manage volunteer background check verifications</p>
            </div>
            <button
              onClick={() => setShowInitiateModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="initiate-check-button"
            >
              <Plus className="w-4 h-4" />
              Initiate Check
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666]" />
                <input
                  type="text"
                  placeholder="Search by volunteer name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input-field"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="expired">Expired</option>
                <option value="revoked">Revoked</option>
              </select>
            </div>
          </div>
        </div>

        {/* Checks List */}
        {loading ? (
          <div className="card text-center py-12">
            <Loader2 className="w-8 h-8 text-[#8B2E3C] animate-spin mx-auto" />
          </div>
        ) : filteredChecks.length === 0 ? (
          <div className="card text-center py-12">
            <Shield className="w-12 h-12 text-[#E9DDCF] mx-auto mb-4" />
            <p className="text-[#666666]">No background checks found</p>
            <button
              onClick={() => setShowInitiateModal(true)}
              className="btn-primary mt-4"
            >
              Initiate First Check
            </button>
          </div>
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b-2 border-[#E9DDCF]">
                  <th className="text-left py-3 px-4 font-semibold">Volunteer</th>
                  <th className="text-left py-3 px-4 font-semibold">Clearance Level</th>
                  <th className="text-center py-3 px-4 font-semibold">Status</th>
                  <th className="text-left py-3 px-4 font-semibold">Provider</th>
                  <th className="text-left py-3 px-4 font-semibold">Expiration</th>
                  <th className="text-center py-3 px-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredChecks.map((check) => (
                  <tr key={check.id} className="border-b border-[#E9DDCF] hover:bg-[#FBF4EA]">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#E9DDCF] flex items-center justify-center">
                          <User className="w-4 h-4 text-[#8B2E3C]" />
                        </div>
                        <div>
                          <div className="font-medium">{check.volunteer_name}</div>
                          <div className="text-xs text-[#666666]">{check.volunteer_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {CLEARANCE_LEVELS.find(l => l.value === check.clearance_level)?.label || check.clearance_level}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {getStatusBadge(check.status)}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {check.background_check_provider || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {check.expiration_date || '-'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => openEditModal(check)}
                        className="text-[#8B2E3C] hover:underline text-sm"
                      >
                        {check.status === 'pending' ? 'Complete' : 'Edit'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Initiate Check Modal */}
      {showInitiateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#222222]">Initiate Background Check</h2>
                <button onClick={() => setShowInitiateModal(false)} className="text-[#666666] hover:text-[#222222]">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={handleInitiateCheck}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Volunteer Email *</label>
                    <input
                      type="email"
                      className="input-field"
                      value={initiateData.volunteer_id}
                      onChange={(e) => setInitiateData(prev => ({ ...prev, volunteer_id: e.target.value }))}
                      placeholder="volunteer@example.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Clearance Level *</label>
                    <select
                      className="input-field"
                      value={initiateData.clearance_level}
                      onChange={(e) => setInitiateData(prev => ({ ...prev, clearance_level: e.target.value }))}
                      required
                    >
                      {CLEARANCE_LEVELS.map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Background Check Provider</label>
                    <input
                      type="text"
                      className="input-field"
                      value={initiateData.background_check_provider}
                      onChange={(e) => setInitiateData(prev => ({ ...prev, background_check_provider: e.target.value }))}
                      placeholder="e.g., Sterling, Checkr"
                    />
                  </div>
                </div>

                <div className="flex gap-3 justify-end mt-6">
                  <button type="button" onClick={() => setShowInitiateModal(false)} className="btn-outline">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Initiate Check
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Check Modal */}
      {showEditModal && selectedCheck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#222222]">
                  {selectedCheck.status === 'pending' ? 'Complete Background Check' : 'Edit Background Check'}
                </h2>
                <button onClick={() => setShowEditModal(false)} className="text-[#666666] hover:text-[#222222]">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-[#FBF4EA] rounded-lg">
                <div className="font-medium">{selectedCheck.volunteer_name}</div>
                <div className="text-sm text-[#666666]">{selectedCheck.volunteer_email}</div>
              </div>
              
              <form onSubmit={handleUpdateCheck}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Status</label>
                    <select
                      className="input-field"
                      value={editData.status}
                      onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="pending">Pending</option>
                      <option value="verified">Verified</option>
                      <option value="expired">Expired</option>
                      <option value="revoked">Revoked</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Clearance Level</label>
                    <select
                      className="input-field"
                      value={editData.clearance_level}
                      onChange={(e) => setEditData(prev => ({ ...prev, clearance_level: e.target.value }))}
                    >
                      {CLEARANCE_LEVELS.map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Completion Date</label>
                    <input
                      type="date"
                      className="input-field"
                      value={editData.completion_date}
                      onChange={(e) => setEditData(prev => ({ ...prev, completion_date: e.target.value }))}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Expiration Date</label>
                    <input
                      type="date"
                      className="input-field"
                      value={editData.expiration_date}
                      onChange={(e) => setEditData(prev => ({ ...prev, expiration_date: e.target.value }))}
                    />
                  </div>
                </div>
                
                <h3 className="font-semibold text-[#222222] mt-6 mb-4">Verification Checks</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { key: 'nh_criminal_history_check', label: 'NH Criminal History' },
                    { key: 'national_criminal_check', label: 'National Criminal Check' },
                    { key: 'sex_offender_registry_check', label: 'Sex Offender Registry' },
                    { key: 'child_abuse_registry_check', label: 'Child Abuse Registry' },
                    { key: 'vulnerable_adult_registry_check', label: 'Vulnerable Adult Registry' },
                    { key: 'motor_vehicle_check', label: 'Motor Vehicle Check' }
                  ].map(check => (
                    <div key={check.key}>
                      <label className="block text-sm font-medium mb-2">{check.label}</label>
                      <select
                        className="input-field"
                        value={editData[check.key]}
                        onChange={(e) => setEditData(prev => ({ ...prev, [check.key]: e.target.value }))}
                      >
                        <option value="not_required">Not Required</option>
                        <option value="pending">Pending</option>
                        <option value="verified">Verified</option>
                      </select>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 justify-end mt-6">
                  <button type="button" onClick={() => setShowEditModal(false)} className="btn-outline">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackgroundCheckManagement;
